import { useEffect, useState } from "react";
import { StandardModal } from "@/components/StandardModal";
import { PeriodRangeCalendar, isoFromState } from "@/components/PeriodRangeCalendar";
import { Button } from "@/components/ui/button";
import { FilterSelect } from "@/components/ui/filter-select";
import { Label } from "@/components/ui/label";
import { useClientConfig } from "@/contexts/ClientConfigContext";
import { toast } from "@/lib/appToast";
import { BTN_OUTLINE_ACTION, BTN_PAGE_PRIMARY_ACTION } from "@/lib/buttonStyles";
import {
  MODAL_BODY_CLASS,
  MODAL_FOOTER_STICKY_MOBILE_CLASS,
  MODAL_PANEL_MAX_HEIGHT_CLASS,
} from "@/lib/modalStyles";
import type { VendasFiscalExportFormat } from "@/lib/vendasFiscalExport";

export type ExportarVendasFiscalParams = {
  inicio: string;
  fim: string;
  formato: VendasFiscalExportFormat;
};

type ModalExportarVendasFiscalProps = {
  open: boolean;
  initialInicio: string;
  initialFim: string;
  onClose: () => void;
  onExport: (params: ExportarVendasFiscalParams) => Promise<boolean>;
};

export function ModalExportarVendasFiscal({
  open,
  initialInicio,
  initialFim,
  onClose,
  onExport,
}: ModalExportarVendasFiscalProps) {
  const { config } = useClientConfig();
  const [inicio, setInicio] = useState(initialInicio);
  const [fim, setFim] = useState(initialFim);
  const [inicioMonth, setInicioMonth] = useState(() => isoFromState(initialInicio));
  const [fimMonth, setFimMonth] = useState(() => isoFromState(initialFim));
  const [formato, setFormato] = useState<VendasFiscalExportFormat>("xml");
  const [loading, setLoading] = useState(false);
  const canExport = inicio.length === 10 && fim.length === 10 && !loading;

  useEffect(() => {
    if (!open) return;
    setInicio(initialInicio);
    setFim(initialFim);
    setInicioMonth(isoFromState(initialInicio));
    setFimMonth(isoFromState(initialFim));
    setFormato("xml");
    setLoading(false);
  }, [open, initialInicio, initialFim]);

  const handleInicioSelect = (iso: string) => {
    setInicio(iso);
    if (fim.length === 10 && iso > fim) setFim(iso);
  };

  const handleFimSelect = (iso: string) => {
    setFim(iso);
    if (inicio.length === 10 && iso < inicio) setInicio(iso);
  };

  const handleExport = async () => {
    if (!canExport) {
      toast.error("Informe data inicio e fim.");
      return;
    }
    if (inicio > fim) {
      toast.error("A data inicial deve ser menor ou igual a data final.");
      return;
    }
    setLoading(true);
    try {
      const exported = await onExport({ inicio, fim, formato });
      if (exported) onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardModal
      open={open}
      title="Exportar vendas"
      onClose={onClose}
      closeDisabled={loading}
      panelClassName={`!max-w-xl ${MODAL_PANEL_MAX_HEIGHT_CLASS}`}
    >
      <div className={MODAL_BODY_CLASS}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <PeriodRangeCalendar
            viewMonth={inicioMonth}
            onViewMonthChange={setInicioMonth}
            selectedIso={inicio}
            onSelect={handleInicioSelect}
            primaryColor={config.primary_color}
            ariaLabel="Calendario da data de inicio"
          />
          <PeriodRangeCalendar
            viewMonth={fimMonth}
            onViewMonthChange={setFimMonth}
            selectedIso={fim}
            onSelect={handleFimSelect}
            primaryColor={config.primary_color}
            ariaLabel="Calendario da data final"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="exportar-vendas-arquivo">Arquivo</Label>
          <FilterSelect
            id="exportar-vendas-arquivo"
            value={formato}
            onChange={(e) => setFormato(e.target.value as VendasFiscalExportFormat)}
            disabled={loading}
            aria-label="Arquivo"
            className="max-w-[180px]"
          >
            <option value="xml">XML</option>
            <option value="excel">Excel</option>
          </FilterSelect>
        </div>

        <div className={`flex justify-end gap-2 pt-2 ${MODAL_FOOTER_STICKY_MOBILE_CLASS}`}>
          <Button
            type="button"
            variant="outline"
            className={BTN_OUTLINE_ACTION}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className={BTN_PAGE_PRIMARY_ACTION}
            onClick={() => void handleExport()}
            disabled={!canExport}
          >
            {loading ? "Exportando..." : "Exportar"}
          </Button>
        </div>
      </div>
    </StandardModal>
  );
}
